'use client';

import { c as _c } from "react/compiler-runtime";
import { useFrame } from '@react-three/fiber';
import { cubicBezier } from 'motion/react';
import { type RefObject, useRef } from 'react';
import type { Group } from 'three';
import { PowerRing } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/webgpu/backdrop/power-ring';
import type { RingColor } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/webgpu/backdrop/power-ring/types';
import { ENGINE_STAGE_CONFIG } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/webgpu/engine-stage/config';
import type { EngineStageConfig } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/webgpu/engine-stage/config/types';
import { ENGINE_STAGE_RENDER_ORDER } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/webgpu/engine-stage/constants';
import type { MutableRef } from '@/app/lcv/components/sections/spartan/shared/webgpu/canvas-basic/types';

// Match the engineFloat segment easing; phase comes from its CSS timeline.
const liftEase = cubicBezier(0.42, 0, 0.3, 1);
const returnEase = cubicBezier(0.5, 0, 0.3, 1);
const storyBreathEase = cubicBezier(0.455, 0.03, 0.515, 0.955);
export interface EngineAuraProps {
  // 0..1. Raised by whichever slide owns the engine, once that engine has
  // settled -- see the slides. The aura ignites as the engine lands.
  visibilityRef: MutableRef<number>;
  // The field's colour, driven live. Left unset it holds `baseColor`.
  colorRef?: MutableRef<RingColor>;
  // Draw while invisible so `compileAsync` -- which skips invisible
  // materials -- builds this pipeline off the critical frame.
  warmUp?: boolean;
  config?: EngineStageConfig;
  isStory?: boolean;
  // Was never forwarded, so the ring kept breathing under
  // prefers-reduced-motion even with everything around it frozen.
  isAnimating?: boolean;
  // Scales the ring's own clock. Pass a caller's playback speed here so the
  // field slows with whatever it is meant to stay in step with.
  timeScale?: number;
  // Bumped to restart the field from the top. See PowerRingProps.resetKey.
  resetKey?: number;
  introFloatAnimationRef?: RefObject<Animation | null>;
  storyBreathingAnimationRef?: RefObject<Animation | null>;
}
export const EngineAura = t0 => {
  const $ = _c(41);
  const {
    visibilityRef,
    colorRef,
    warmUp: t1,
    config: t2,
    isStory: t3,
    isAnimating: t4,
    timeScale: t5,
    resetKey: t6,
    introFloatAnimationRef,
    storyBreathingAnimationRef
  } = t0;
  const warmUp = t1 === undefined ? false : t1;
  const config = t2 === undefined ? ENGINE_STAGE_CONFIG : t2;
  const isStory = t3 === undefined ? false : t3;
  const isAnimating = t4 === undefined ? true : t4;
  const timeScale = t5 === undefined ? 1 : t5;
  const resetKey = t6 === undefined ? 0 : t6;
  const aura = isStory ? config.storyAura : config.aura;
  const breathingGroup = useRef(null);
  let t7;
  if ($[0] !== introFloatAnimationRef || $[1] !== isAnimating || $[2] !== isStory || $[3] !== storyBreathingAnimationRef) {
    t7 = () => {
      if (!breathingGroup.current) {
        return;
      }
      if (!isAnimating) {
        breathingGroup.current.scale.setScalar(1);
        return;
      }
      const animation = isStory ? storyBreathingAnimationRef?.current : introFloatAnimationRef?.current;
      const elapsed = animation?.currentTime;
      const timing = animation?.effect?.getTiming();
      const duration = timing?.duration;
      if (typeof elapsed !== "number" || typeof duration !== "number" || duration <= 0) {
        breathingGroup.current.scale.setScalar(1);
        return;
      }
      const phase = ((elapsed - (timing?.delay ?? 0)) % duration + duration) % duration / duration;
      if (isStory) {
        const breath = phase < 0.5 ? storyBreathEase(phase * 2) : 1 - storyBreathEase((phase - 0.5) * 2);
        breathingGroup.current.scale.setScalar(1 + 0.29 * breath);
        return;
      }
      const lift = phase < 0.65 ? liftEase(phase / 0.65) : 1 - returnEase((phase - 0.65) / 0.35);
      breathingGroup.current.scale.setScalar(1 - 0.06 * lift);
    };
    $[0] = introFloatAnimationRef;
    $[1] = isAnimating;
    $[2] = isStory;
    $[3] = storyBreathingAnimationRef;
    $[4] = t7;
  } else {
    t7 = $[4];
  }
  useFrame(t7);
  const t8 = aura.sizeMultiplier * aura.liftMultiplier;
  let t9;
  if ($[5] !== aura.baseColor || $[6] !== aura.breathGammaPeak || $[7] !== aura.breathMax || $[8] !== aura.breathMin || $[9] !== aura.breathPeakAt || $[10] !== aura.breathSeconds || $[11] !== aura.brightness || $[12] !== aura.coreFeather || $[13] !== aura.coreOpacity || $[14] !== aura.coreRadius || $[15] !== aura.flowSpeed || $[16] !== aura.frequency || $[17] !== aura.gamma || $[18] !== aura.noiseScale || $[19] !== aura.octaves || $[20] !== aura.opacity || $[21] !== aura.pulseSpeed || $[22] !== aura.ringScale || $[23] !== aura.sharpness || $[24] !== aura.sizeMultiplier || $[25] !== aura.swirlSpeed || $[26] !== aura.trailDecay || $[27] !== aura.trailLength || $[28] !== aura.trailSamples || $[29] !== aura.vignetteFeather || $[30] !== aura.vignetteRadius || $[31] !== aura.warpStrength || $[32] !== aura.zoom || $[33] !== colorRef || $[34] !== isAnimating || $[35] !== resetKey || $[36] !== t8 || $[37] !== timeScale || $[38] !== visibilityRef || $[39] !== warmUp) {
    t9 = <group ref={breathingGroup}><PowerRing visibilityRef={visibilityRef} colorRef={colorRef} warmUp={warmUp} renderOrder={ENGINE_STAGE_RENDER_ORDER.aura} size={aura.sizeMultiplier} offsetAlongNormal={t8} zoom={aura.zoom} flowSpeed={aura.flowSpeed} noiseScale={aura.noiseScale} warpStrength={aura.warpStrength} swirlSpeed={aura.swirlSpeed} ringFrequency={aura.frequency} ringPulseSpeed={aura.pulseSpeed} baseColor={aura.baseColor} vignetteFeather={aura.vignetteFeather} opacityScale={aura.opacity} octaves={aura.octaves} trailLength={aura.trailLength} trailDecay={aura.trailDecay} trailSamples={aura.trailSamples} ringScale={aura.ringScale} brightness={aura.brightness} isAnimating={isAnimating} timeScale={timeScale} resetKey={resetKey} breathMin={aura.breathMin} breathMax={aura.breathMax} breathSeconds={aura.breathSeconds} breathPeakAt={aura.breathPeakAt} breathGammaPeak={aura.breathGammaPeak} gamma={aura.gamma} ringSharpness={aura.sharpness} vignetteRadius={aura.vignetteRadius} coreOpacity={aura.coreOpacity} coreRadius={aura.coreRadius} coreFeather={aura.coreFeather} /></group>;
    $[5] = aura.baseColor;
    $[6] = aura.breathGammaPeak;
    $[7] = aura.breathMax;
    $[8] = aura.breathMin;
    $[9] = aura.breathPeakAt;
    $[10] = aura.breathSeconds;
    $[11] = aura.brightness;
    $[12] = aura.coreFeather;
    $[13] = aura.coreOpacity;
    $[14] = aura.coreRadius;
    $[15] = aura.flowSpeed;
    $[16] = aura.frequency;
    $[17] = aura.gamma;
    $[18] = aura.noiseScale;
    $[19] = aura.octaves;
    $[20] = aura.opacity;
    $[21] = aura.pulseSpeed;
    $[22] = aura.ringScale;
    $[23] = aura.sharpness;
    $[24] = aura.sizeMultiplier;
    $[25] = aura.swirlSpeed;
    $[26] = aura.trailDecay;
    $[27] = aura.trailLength;
    $[28] = aura.trailSamples;
    $[29] = aura.vignetteFeather;
    $[30] = aura.vignetteRadius;
    $[31] = aura.warpStrength;
    $[32] = aura.zoom;
    $[33] = colorRef;
    $[34] = isAnimating;
    $[35] = resetKey;
    $[36] = t8;
    $[37] = timeScale;
    $[38] = visibilityRef;
    $[39] = warmUp;
    $[40] = t9;
  } else {
    t9 = $[40];
  }
  return t9;
};